import React, { useMemo, useRef } from "react";
import styled from "@emotion/styled";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip as ChartTooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";
import NPSRing from "./NPSRing";
import { CYCLE_COLORS } from "../../../utils/Service/colors";
import CardHeader from "../../../components/common/CardHeader";
import { Spin } from "antd";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  ChartTooltip,
  ChartDataLabels,
);

const emptyStats = {
  promoters_count: 0,
  passives_count: 0,
  detractors_count: 0,
  total_replies: 0,
  average_nps_percentage: 0,
};

const getNps = (stats) => {
  if (!stats) return 0;
  if (
    stats.average_nps_percentage !== undefined &&
    stats.average_nps_percentage !== null
  ) {
    return stats.average_nps_percentage;
  }
  if (!stats.total_replies) return 0;
  return Math.round(
    ((stats.promoters_count - stats.detractors_count) / stats.total_replies) *
      100,
  );
};

const NPSSection = ({
  cycleIds,
  cycles,
  npsLoading,
  npsData,
  downloading,
  downloadBarChartAsPNG,
}) => {
  const selectedCycleIds = cycleIds || [];
  const NPSRef = useRef(null);

  const cycleById = useMemo(() => {
    const map = {};
    cycles.forEach((c) => (map[c.id] = c));
    return map;
  }, [cycles]);

  const statsByCycleId = useMemo(() => {
    const map = {};
    (npsData || []).forEach((item) => {
      map[item.audit_cycle_id] = item;
    });
    return map;
  }, [npsData]);

  const selectedCards = selectedCycleIds
    .map((id, index) => {
      const cycle = cycleById[id];

      if (!cycle) return null;

      const cycleColor = CYCLE_COLORS[index % CYCLE_COLORS.length];

      return {
        ...cycle,
        id,
        color: cycleColor.main,
        bgColor: cycleColor.bg,
        stats: statsByCycleId[id] || emptyStats,
      };
    })
    .filter(Boolean);

  const chronological = [...selectedCards].sort(
    (a, b) => cycles.indexOf(cycleById[a.id]) - cycles.indexOf(cycleById[b.id]),
  );

  const deltaFor = (cardId) => {
    const idx = chronological.findIndex((c) => c.id === cardId);
    if (idx <= 0) return null;
    const current = getNps(chronological[idx].stats);
    const prev = chronological[idx - 1];
    return {
      diff: Math.round(current - getNps(prev.stats)),
      against: prev.name,
    };
  };

  const trendChartData = {
    labels: chronological.map((c) => c.name),
    datasets: [
      {
        data: chronological.map((c) => Math.round(getNps(c.stats))),
        borderColor: "#8A94A6",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        backgroundColor: (ctx) => {
          const { chart } = ctx;
          const { chartArea } = chart;
          if (!chartArea) return "transparent";
          const gradient = chart.ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(0, 125, 193, 0.14)");
          gradient.addColorStop(1, "rgba(0, 125, 193, 0)");
          return gradient;
        },
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBorderWidth: 2,
        pointBackgroundColor: chronological.map((c) => c.color),
        pointBorderColor: "#ffffff",
        datalabels: {
          color: (ctx) => chronological[ctx.dataIndex]?.color || "#353E4C",
          anchor: "end",
          align: "top",
          offset: 10,
          font: { weight: 700, size: 12 },
          formatter: (value) => `${value}%`,
        },
      },
    ],
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 28, right: 16, bottom: 4, left: 4 } },
    interaction: { mode: "nearest", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#16325C",
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: { label: (ctx) => `NPS ${ctx.parsed.y}%` },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: "#8A94A6", font: { size: 12 } },
      },
      y: {
        display: false,
        suggestedMin: (ctx) => {
          const values = ctx.chart.data.datasets[0].data;
          return values.length ? Math.min(...values) - 15 : 0;
        },
        suggestedMax: (ctx) => {
          const values = ctx.chart.data.datasets[0].data;
          return values.length ? Math.max(...values) + 15 : 100;
        },
      },
    },
  };

  return (
    <Wrapper className="dashboard-card shadow p-3" ref={NPSRef}>
      <CardHeader
        title="Net Promoter Score – Comparative View"
        subtitle="Showing the audit cycles selected above"
        selectedCards={selectedCards}
        downloading={downloading}
        onDownloadClick={() => downloadBarChartAsPNG(NPSRef, "nps")}
      />

      <div className="upAuditsCard p-0 m-0">
        {npsLoading ? (
          <LoadingState>
            <Spin />
          </LoadingState>
        ) : selectedCards.length === 0 ? (
          <EmptyState style={{ padding: "3rem 0" }}>
            Select at least one audit cycle to see data
          </EmptyState>
        ) : (
          <Grid columns={selectedCards.length}>
            <MetricsColHeader className="metrics-column">
              <div className="metrics-header">
                <p>Metrics</p>
              </div>
              <div className="overall-nps">
                <div className="nps-icon">%</div>
                <div className="nps-text">
                  <p className="nps-title">Overall NPS Score</p>
                  <p className="nps-subtitle">(vs. Previous Cycle)</p>
                </div>
              </div>
            </MetricsColHeader>

            {selectedCards.map((c) => {
              const nps = getNps(c.stats);
              const delta = deltaFor(c.id);
              return (
                <CardHead key={c.id} color={c.color}>
                  <CycleName color={c.color}>{c.name}</CycleName>
                  <NPSRing percentage={nps} color={c.color} />
                  <DeltaText positive={delta ? delta.diff >= 0 : true}>
                    {delta
                      ? `${delta.diff >= 0 ? "↑" : "↓"} ${Math.abs(delta.diff)}% vs ${delta.against}`
                      : "—"}
                  </DeltaText>
                </CardHead>
              );
            })}

            <MetricLabel>
              <span>🙂</span> Promoters (9-10)
            </MetricLabel>
            {selectedCards.map((c) => (
              <MetricValue key={c.id} color="#3CB878">
                {c.stats.promoters_count} (
                {c.stats.total_replies
                  ? Math.round(
                      (c.stats.promoters_count / c.stats.total_replies) * 100,
                    )
                  : 0}
                %)
              </MetricValue>
            ))}

            <MetricLabel>
              <span>😐</span> Passives (7-8)
            </MetricLabel>
            {selectedCards.map((c) => (
              <MetricValue key={c.id} color="#F2994A">
                {c.stats.passives_count} (
                {c.stats.total_replies
                  ? Math.round(
                      (c.stats.passives_count / c.stats.total_replies) * 100,
                    )
                  : 0}
                %)
              </MetricValue>
            ))}

            <MetricLabel>
              <span>🙁</span> Detractors (0-6)
            </MetricLabel>
            {selectedCards.map((c) => (
              <MetricValue key={c.id} color="#EB5757">
                {c.stats.detractors_count} (
                {c.stats.total_replies
                  ? Math.round(
                      (c.stats.detractors_count / c.stats.total_replies) * 100,
                    )
                  : 0}
                %)
              </MetricValue>
            ))}

            <MetricLabel>
              <span>👥</span> Total Responses
            </MetricLabel>
            {selectedCards.map((c) => (
              <MetricValue key={c.id}>{c.stats.total_replies}</MetricValue>
            ))}

            <MetricLabel bold>
              <span>%</span> Overall NPS Score
            </MetricLabel>
            {selectedCards.map((c) => (
              <MetricValue key={c.id} bold color={c.color}>
                {Math.round(getNps(c.stats))}%
              </MetricValue>
            ))}
          </Grid>
        )}
      </div>

      {selectedCards.length > 1 && (
        <TrendSection className="upAuditsCard p-3 m-0">
          <TrendHeader>
            <TrendTitle>NPS Trend</TrendTitle>
            <TrendSubtitle>Over Time</TrendSubtitle>
          </TrendHeader>
          <ChartBox>
            <Line data={trendChartData} options={trendChartOptions} />
          </ChartBox>
        </TrendSection>
      )}

      <FootNote>
        NPS Score is calculated as % Promoters (9-10) minus % Detractors (0-6)
      </FootNote>
    </Wrapper>
  );
};

/* ---------------- styled ---------------- */

const Wrapper = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem;
  font-family: Roboto, sans-serif;
`;

const EmptyState = styled.div`
  color: #8a94a6;
  font-size: 0.85rem;
  text-align: center;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  padding: 3rem 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 220px) repeat(
      ${(props) => props.columns},
      minmax(140px, 1fr)
    );
  border: 1px solid #eaecef;
  border-radius: 12px;
  overflow: hidden;

  > * {
    border-bottom: 1px solid #eaecef;
    border-right: 1px solid #eaecef;
    padding: 0.9rem 1rem;
    display: flex;
    align-items: center;
  }
  > *:nth-last-of-type(-n + ${(props) => props.columns + 1}) {
    border-bottom: none;
  }
`;

const MetricsColHeader = styled.div`
  background: #f8f9fb;
  font-weight: 700;
  color: #16325c;
`;

const CardHead = styled.div`
  flex-direction: column;
  gap: 0.6rem;
  background: #fcfcfd;
  border-top: 3px solid ${(props) => props.color};
  padding-top: 1.1rem !important;
`;

const CycleName = styled.span`
  font-weight: 700;
  color: ${(props) => props.color};
  font-size: 0.95rem;
`;

const DeltaText = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${(props) => (props.positive ? "#3CB878" : "#EB5757")};
`;

const MetricLabel = styled.div`
  color: #353e4c;
  font-size: 0.85rem;
  font-weight: ${(props) => (props.bold ? 700 : 500)};
  gap: 0.5rem;
`;

const MetricValue = styled.div`
  color: ${(props) => props.color || "#16325C"};
  font-weight: ${(props) => (props.bold ? 700 : 600)};
  font-size: 0.9rem;
  justify-content: center;
`;

const FootNote = styled.div`
  margin-top: 1rem;
  background: #f8f9fb;
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  font-size: 0.78rem;
  color: #8a94a6;
`;

const TrendTitle = styled.p`
  color: #16325c;
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1.2;
  margin: 0;
`;

const TrendSubtitle = styled.p`
  color: #8a94a6;
  font-size: 0.8rem;
  margin: 0.15rem 0 0;
`;

const TrendSection = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.5rem;
  margin-top: 1.5rem;
  padding: 1.25rem 1.5rem;
  border: 1px solid #eaecef;
  border-radius: 12px;
  background: #fcfcfd;
`;

const TrendHeader = styled.div`
  flex: 0 0 auto;
  width: 110px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ChartBox = styled.div`
  flex: 1;
  min-width: 0;
  height: 220px;
  position: relative;
`;

export default NPSSection;
